// app.ts
import { defineStore } from 'pinia'
import { OUTLINE_DRAFT_STORAGE_KEY } from '@/utils/createDraftCache'

// 定义 AndrioInfo 接口
interface AndrioInfo {
  barHeight: number; // 顶部导航高度
  uid: string; // uid
  token: string; // token
  version: string; // 版本号
  versionCode: string; // 版本号数字
  pkg: string; // 包名称
  platform: string; // 设备类型
  di: string; // 设备id
  oi: string; // 设备oaid
  dn: string; // 设备名称deviceName
}

// 定义 AppStore 状态接口
interface AppStoreState {
  userInfo: string; // 登录信息存储字段
  sizeMap: string[]; // 尺寸映射
  mobile: boolean; // 是否是移动端
  title: string; // 标题
  pageLoading: boolean; // 路由跳转loading
  MainHeight: number; // 主要高度
  showAside: boolean; // 是否显示侧边栏
  andrioInfo: AndrioInfo; // Android/iOS 信息
  outlineParams: any; // 大纲参数
  showLoginModal: boolean;
  integralModal: boolean;
  tabBarHeight: number;
  activityInfo: Record<string, any>;
}

// 定义 AppStore 的 getters
interface AppStoreGetters {
  getAndrio(): AndrioInfo;
  getBarHeight(): number;
  getVersionCode(): string;
  getMainHeight(): number;
  getShowAside(): boolean;
  getLoginModal(): boolean;
  getIntegralModal(): boolean;
  getTabBarHeight(): number;
  getActivityInfo(): Record<string, any>;
}

// 定义 AppStore 的 actions
interface AppStoreActions {
  setAndrioInfo(data: Partial<AndrioInfo>): void;
  setToken(data: string): void;
  setMainHeight(height: number): void;
  setLoginModal(data: boolean): void;
  setIntegralModal(data: boolean): void;
  setTabBarHeight(data: number): void;
  setActivityInfo(data: Record<string, any>): void;
  clearOutlineFormDraft(): void;
}

// 使用类型定义的 store
export const useAppStore = defineStore<'app', AppStoreState, AppStoreGetters, AppStoreActions>('app', {
  state: (): AppStoreState => ({
    userInfo: 'yidian',
    sizeMap: ['default', 'large', 'small'],
    mobile: false,
    title: import.meta.env.VITE_APP_TITLE,
    pageLoading: false,
    MainHeight: 0,
    showAside: true,
    tabBarHeight: 0,
    andrioInfo: {
      barHeight: 40,
      uid: '',
      token: '',
      version: '1.0.0',
      versionCode: '100',
      pkg: '',
      platform: '',
      di: '',
      oi: '',
      dn: '',
    },
    outlineParams:{
      inputText:'',
      pageCount:'20页以内',
      language:'zh',
      audiences:'',
      textVolume:'详细',
      scene:'',
      fujian:'',
    },
    showLoginModal: false,
    integralModal:false, //积分不足弹窗
    activityInfo: {},

  }),
  
  getters: {
    getAndrio(): AndrioInfo {
      return this.andrioInfo
    },
    getBarHeight(): number {
      return this.andrioInfo.barHeight
    },
    getVersionCode(): string {
      return this.andrioInfo.versionCode
    },
    getMainHeight(): number {
      return this.MainHeight
    },
    getShowAside(): boolean {
      return this.showAside
    },
    getLoginModal(): boolean {      
      return this.showLoginModal      
    },
    getIntegralModal(){
      return this.integralModal
    },
    getTabBarHeight(): number {
      return this.tabBarHeight
    },
    getActivityInfo(): Record<string, any> {
      return this.activityInfo
    },

  },
  
  actions: {
    setAndrioInfo(data: Partial<AndrioInfo>) {
      this.andrioInfo = { ...this.andrioInfo, ...data }
    },
    setToken(data: string) {
      this.andrioInfo.token = data
    },
    setMainHeight(height: number) {
      this.MainHeight = height
    },
    setShowAside(data: boolean) {
      this.showAside = data
    },
    setLoginModal(data: boolean){
      this.showLoginModal = data
    },
    setIntegralModal(data: boolean){
      this.integralModal = data
    },
    setTabBarHeight(data: number){
      this.tabBarHeight = data + 10
    },
    setActivityInfo(data: Record<string, any>) {
      this.activityInfo = data || {}
    },
    /** 从移动端大纲生成页返回首页时：清空主题/页数语言/附件草稿（与 MobileHome.init 一致） */
    clearOutlineFormDraft() {
      this.outlineParams.inputText = ''
      this.outlineParams.pageCount = '20页以内'
      this.outlineParams.language = 'zh'
      this.outlineParams.audiences = ''
      this.outlineParams.textVolume = '详细'
      this.outlineParams.scene = ''
      this.outlineParams.fujian = ''
      try {
        localStorage.removeItem(OUTLINE_DRAFT_STORAGE_KEY)
      } catch {
        /* ignore */
      }
    },
  },
  
      // 持久化配置
  persist: {
    // 存储方式，默认为 localStorage
    storage: localStorage,
    
    // 持久化字段，只保存特定字段
    pick: ['andrioInfo', 'tabBarHeight'],
    
    // 或者使用 omit 排除不保存的字段
    // omit: ['someTemporaryField'],
    
    // 自定义存储的 key
    key: 'app',
  }
})